import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Check, LogOut, ShieldCheck, UserCog, Timer } from "lucide-react";
import { usePersona, personas, identityProvider } from "@/context/PersonaContext";
import DelegateModal from "@/components/DelegateModal";

/**
 * UserMenu · avatar + dropdown containing persona switcher, active
 * signing session countdown, delegate authority action, active
 * delegation status, and sign out.
 */
export default function UserMenu() {
  const {
    active,
    setActiveId,
    signOut,
    signatureFresh,
    lastAuthAtLabel,
    formatSessionCountdown,
    delegation,
    revokeDelegation,
    delegationExpiresLabel,
    formatDelegationCountdown,
    canDelegate,
  } = usePersona();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!active) return null;

  const switchTo = (personaId) => {
    setActiveId(personaId);
    setOpen(false);
    navigate(personas[personaId].home);
  };

  const doSignOut = () => {
    signOut();
    setOpen(false);
    navigate("/signin");
  };

  const activeHasDelegation =
    delegation &&
    (delegation.from === active.id || delegation.to === active.id);
  const isDelegatee =
    delegation && delegation.to === active.id;
  const isDelegator =
    delegation && delegation.from === active.id;

  return (
    <>
      <div className="flex items-center gap-2" ref={rootRef}>
        {/* Signing session pill · visible when fresh */}
        {signatureFresh && (
          <span
            className="hidden lg:inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[11px]"
            style={{
              background: "#ECFDF5",
              borderColor: "var(--mos-green-border)",
              color: "#065F46",
            }}
            data-testid="session-pill"
            title={`Signed at ${lastAuthAtLabel} · expires in ${formatSessionCountdown()}`}
          >
            <ShieldCheck size={11} />
            <span className="uppercase tracking-wider font-semibold">SIG</span>
            <span
              className="mos-mono"
              data-testid="session-pill-countdown"
            >
              {formatSessionCountdown()}
            </span>
          </span>
        )}

        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2.5 pl-1 pr-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
            data-testid="user-menu-trigger"
            title={`${active.name} · ${active.email}\nFederated via ${identityProvider}`}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <span
              className="h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-semibold mos-mono relative"
              style={{
                background: active.id === "jokafor" ? "#1E3A5F" : "#5B7191",
                color: "#FFFFFF",
              }}
              data-testid="user-avatar"
            >
              {active.initials}
              {isDelegatee && (
                <span
                  className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2"
                  style={{
                    background: "var(--mos-accent)",
                    borderColor: "var(--mos-surface)",
                  }}
                  aria-label="holds delegated authority"
                  data-testid="delegatee-dot"
                />
              )}
            </span>
            <span className="hidden lg:flex flex-col items-start leading-tight">
              <span
                className="text-[12.5px] font-medium text-slate-900"
                data-testid="user-name"
              >
                {active.name}
              </span>
              <span
                className="text-[10.5px] text-slate-500"
                data-testid="user-role"
              >
                {active.role}
                {isDelegatee && (
                  <span
                    className="ml-1 text-[color:var(--mos-accent)] font-medium"
                    data-testid="user-role-delegated"
                  >
                    + delegated
                  </span>
                )}
              </span>
            </span>
            <ChevronDown size={13} className="text-slate-400" />
          </button>

          {open && (
            <div
              role="menu"
              className="absolute right-0 top-full mt-2 w-[320px] rounded-md border shadow-lg z-40 py-2"
              style={{
                background: "var(--mos-surface)",
                borderColor: "var(--mos-border)",
                animation: "mos-fade-in 140ms ease-out both",
              }}
              data-testid="user-menu-panel"
            >
              {/* Signing session line · always shown so buyers see the discipline */}
              <div
                className="mx-3 mb-2 mt-1 rounded-md border px-3 py-2 text-[11.5px]"
                style={{
                  background: signatureFresh ? "#ECFDF5" : "#F8FAFC",
                  borderColor: signatureFresh
                    ? "var(--mos-green-border)"
                    : "var(--mos-border)",
                  color: signatureFresh ? "#065F46" : "var(--mos-text-subtle)",
                }}
                data-testid="menu-session-info"
              >
                {signatureFresh ? (
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={12} />
                    <span>
                      Signing session active · signed{" "}
                      <span className="mos-mono">{lastAuthAtLabel}</span>
                    </span>
                    <span className="ml-auto mos-mono font-medium">
                      {formatSessionCountdown()}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={12} className="text-slate-400" />
                    <span>No active signing session · re-authenticate on next approval</span>
                  </div>
                )}
              </div>

              <div className="px-3 pb-2 pt-1 text-[10.5px] tracking-[0.18em] uppercase font-semibold text-slate-500">
                Switch persona
              </div>
              {Object.values(personas).map((p) => {
                const isActive = active.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => switchTo(p.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
                    data-testid={`user-menu-persona-${p.id}`}
                    role="menuitem"
                  >
                    <span
                      className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold mos-mono flex-none"
                      style={{
                        background: p.id === "jokafor" ? "#1E3A5F" : "#5B7191",
                        color: "#FFFFFF",
                      }}
                    >
                      {p.initials}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[13px] font-medium text-slate-900 truncate">
                        {p.name}
                      </span>
                      <span className="block text-[11.5px] text-slate-500 truncate">
                        {p.role}
                      </span>
                    </span>
                    {isActive && (
                      <Check
                        size={14}
                        style={{ color: "var(--mos-accent)" }}
                        aria-label="active"
                      />
                    )}
                  </button>
                );
              })}

              {/* Delegation section */}
              <div
                className="my-2 border-t"
                style={{ borderColor: "var(--mos-border)" }}
              />
              {activeHasDelegation ? (
                <div className="px-3 py-2" data-testid="menu-delegation-active">
                  <div className="text-[10.5px] tracking-[0.18em] uppercase font-semibold text-slate-500 mb-1.5">
                    Active delegation
                  </div>
                  <div
                    className="rounded-md border px-3 py-2 text-[12px] leading-snug"
                    style={{
                      background: "#EFF3F9",
                      borderColor: "var(--mos-border)",
                      color: "var(--mos-text)",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <UserCog
                        size={12}
                        style={{ color: "var(--mos-accent)" }}
                      />
                      <span className="font-medium">
                        {isDelegator ? "You delegated" : "You hold"}{" "}
                        <span className="mos-mono">
                          {delegation.authority}
                        </span>
                      </span>
                    </div>
                    <div className="mt-1 text-slate-600">
                      {isDelegator
                        ? `to ${personas[delegation.to].name}`
                        : `from ${personas[delegation.from].name}`}
                      {" · expires "}
                      <span className="mos-mono">
                        {delegationExpiresLabel}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-slate-500">
                      <Timer size={11} />
                      <span className="mos-mono" data-testid="menu-delegation-countdown">
                        {formatDelegationCountdown()}
                      </span>
                      {" remaining"}
                    </div>
                    {isDelegator && (
                      <button
                        type="button"
                        onClick={() => {
                          revokeDelegation();
                          setOpen(false);
                        }}
                        className="mt-2 text-[12px] font-medium underline underline-offset-2 hover:no-underline"
                        style={{ color: "var(--mos-red)" }}
                        data-testid="btn-revoke-delegation"
                      >
                        Revoke now
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                canDelegate("production-commitment") && (
                  <button
                    type="button"
                    onClick={() => {
                      setDelegateOpen(true);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="user-menu-delegate"
                    role="menuitem"
                  >
                    <UserCog size={13} className="text-slate-500" />
                    Delegate authority…
                  </button>
                )
              )}

              <div
                className="my-2 border-t"
                style={{ borderColor: "var(--mos-border)" }}
              />
              <button
                type="button"
                onClick={doSignOut}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="user-menu-signout"
                role="menuitem"
              >
                <LogOut size={13} className="text-slate-500" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      <DelegateModal
        open={delegateOpen}
        onClose={() => setDelegateOpen(false)}
        authority="production-commitment"
      />
    </>
  );
}
